import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ReactQuill from 'react-quill';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Table from '../../CommonComponents/Table';
import { useInfiniteScroll } from '../../../Hooks/useInfiniteScroll';
import useAxiosPrivate from '../../../Hooks/useAxiosPrivate';
import { useDebouncedValue } from '../../../Hooks/useDebounceValue';
import AlertContext from '../../../context/AlertContext';
import { complexityid, datatypes, libTypeIds, RESULT_STATUS } from '../../../constants';
import { LibraryContext } from '../../../context/LibraryContext';
import { useDrag, useDrop } from "react-dnd";
import IconButton from '../../CommonComponents/IconButton';
import Button from '../../Button';
import Modal from '../../CommonComponents/Modal';
import ModalBody from '../../CommonComponents/ModalBody';
import QnDeactivateImg from "../../../assets/images/QnDeactivate.png";
import CCQuestionImg from "../../../assets/images/ccQuestions.png";
import InputDropdown from '../../CommonComponents/InputDropdown';
import Toast from '../../CommonComponents/Toast';
import Tooltip from '../../CommonComponents/Tooltip';


const AdminCcSectionQuestions = () => {

    const { type, secid } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { setShowNotify } = useContext(AlertContext);
    const { searchText, editCodingCategories, setEditCodingCategories, openQuestionModal, setOpenQuestionModal, setSearchText, sectionName, setSectionName } = useContext(LibraryContext);

    const axios = useAxiosPrivate();
    const debouncedSearchText = useDebouncedValue(searchText, 500);


    const ITEM_TYPE = "ROW";
    const [isLoading, setIsLoading] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const previousRef = useRef(null);

    const compObj = {
        1: "Easy",
        2: "Medium",
        3: "Hard"
    };


    const [toastType, setToastType] = useState("warning");
    const [openToast, setOpenToast] = useState(false);
    const [errMsg, setErrMsg] = useState("");


    useLayoutEffect(() => {
        setSearchText("");
    }, []);

    const fetchQuestions = useCallback(
        async ({ page, pageSize }) => {
            try {
                console.log("openQuestionModal:", openQuestionModal);
                if (openQuestionModal) {
                    return;
                }
                const url = `node/admin/cc/section/${secid}?name=${debouncedSearchText}&pageNumber=${page}`;
                const { data } = await axios.get(url, {
                    headers: { "Content-type": "application/json" },
                });
                if (data.resultCode === RESULT_STATUS.SUCCESS) {

                    const resdata =
                        typeof data.data === "string" ? JSON.parse(data.data) : data.data;

                    setSectionName(resdata?.name ?? "");

                    const res = typeof resdata?.data === "string" ? JSON.parse(resdata?.data) : resdata?.data;
                    console.log("res:", res);

                    if (!res || res.length == 0) {
                        return { data: [], total: data.count };

                    }
                    const transFormedData = res?.map((item, index) => ({
                        ...item,
                        complexityname: compObj[item.complexity],
                        sno: (page - 1) * pageSize + index + 1,
                    }));
                    previousRef.current = [...sections, transFormedData];
                    return { data: transFormedData, total: data.count };
                } else
                    throw new Error(
                        "Unable to fetch the question list. Please try again later.",
                    );
            } catch (error) {
                let isUnAuth = error?.response?.status === 403;
                if (error?.name === "CanceledError" || error?.name === "AbortError")
                    return;
                if (isUnAuth) {
                    setShowNotify({
                        show: true,
                        msg: "You have been logged-out due to inactivity. Login again.",
                        title: "Warning !",
                        isUnAuth,
                    });
                }
                setOpenToast(true);
                setErrMsg(error.message ?? "Something went wrong");
                setToastType("error");
            }
        },
        [axios, type, setShowNotify, debouncedSearchText, openQuestionModal],
    );

    // console.log("openQuestionModal:Global", openQuestionModal);


    const updateOrderNumberFunction = async () => {
        try {
            setIsLoading(true);
            const response = await axios.put(`node/admin/cc/section/${secid}/reorder`, { codingQuestion: sections });
            if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
                setEditCodingCategories(false);
                setOpenToast(true);
                setErrMsg(response?.data?.data ?? "Order changed succesfully");
                setToastType("success");
            }
            else {
                setSections(previousRef.current[0]);
                throw new Error(response?.data?.error ?? "Something went wrong");
            }
        } catch (error) {
            let isUnAuth = error?.response?.status === 403;
            if (error?.name === "CanceledError" || error?.name === "AbortError")
                return;
            if (isUnAuth) {
                setShowNotify({
                    show: true,
                    msg: "You have been logged-out due to inactivity. Login again.",
                    title: "Warning !",
                    isUnAuth,
                });
            }
            setOpenToast(true);
            setErrMsg(error.message ?? "Something went wrong");
            setToastType("error");
        }
        finally {
            setIsLoading(false);
        }
    }


    const {
        containerRef,
        sentinelRef,
        loading,
        hasMore,
        items: sections,
        setItems: setSections,
    } = useInfiniteScroll({
        fetchFn: fetchQuestions,
        pageSize: 10,
        resetKey: JSON.stringify({ debouncedSearchText, type, openQuestionModal }),
    });


    // const renderActionCol = useCallback(
    //     (col, row) => (
    //         <RowActionMenu
    //             row={row}
    //             editCodingCategories={editCodingCategories}
    //         />
    //     ),
    //     [editCodingCategories],
    // );


    const TABLE_COLUMNS = useMemo(
        () => [
            {
                key: "sno",
                title: "S.No",
            },
            {
                key: "name",
                title: "QUESTION",
            },
            {
                key: "complexityname",
                title: "LEVEL",
            },
        ],
        [type, editCodingCategories],
    );

    const navigateBack = () => {
        navigate(`/library/${type}/cc/section`);
        setEditCodingCategories(false);
    }

    const moveRow = (fromIndex, toIndex) => {
        const newRows = [...sections];

        const dragged = newRows[fromIndex];

        newRows.splice(fromIndex, 1);

        newRows.splice(toIndex, 0, dragged);

        setSections(newRows);
    };

    const deletQuestionFromSection = async (row) => {
        try {
            console.log("row:", row);
            setIsLoading(true);
            const response = await axios.put(`node/admin/cc/section/${row.csid}`);
            if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
                setOpenDeleteModal(false);
                setSelectedRow(null);
                let currentQuestions = sections.filter((p) => p.csid !== row.csid);
                setSections((prev) =>
                    prev.filter((p) => p.csid !== row.csid)
                );
                previousRef.current = currentQuestions;
                setOpenToast(true);
                setErrMsg(response.data.data ?? "");
                setToastType("success");
            }
            else {
                throw new Error(response?.data?.error ?? "Something went wrong");
            }
        } catch (error) {
            let isUnAuth = error?.response?.status === 403;
            if (error?.name === "CanceledError" || error?.name === "AbortError")
                return;
            if (isUnAuth) {
                setShowNotify({
                    show: true,
                    msg: "You have been logged-out due to inactivity. Login again.",
                    title: "Warning !",
                    isUnAuth,
                });
            }
            setOpenToast(true);
            setErrMsg(error.message ?? "Something went wrong");
            setToastType("error");
        }
        finally {
            setIsLoading(false);
        }
    }

    const onCloseModal = () => {
        setOpenDeleteModal(false);
        setSelectedRow(null);
    }

    const onCloseQuestionModal = () => {
        setOpenQuestionModal(false);
    }


    // console.log("sections:", sections);

    return (
        <div className='library-section-question-container'>
            <Toast
                open={openToast}
                onClose={() => setOpenToast(false)}
                message={errMsg}
                type={toastType}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            />
            <header className='library-section-question-header' onClick={() => { navigateBack() }}>
                <div>
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 11L1 6L6 1" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <div className='library-section-question-header-title'>{sectionName}</div>
            </header>
            <Table
                ref={containerRef}
                className={`library-section-question-table`}
                columns={TABLE_COLUMNS}
                data={sections}
                renderRow={(row, rowIndex, columns) => (
                    <DraggableRow
                        key={row.id}
                        row={row}
                        rowIndex={rowIndex}
                        columns={columns}
                        moveRow={moveRow}
                        editCodingCategories={editCodingCategories}
                        setOpenDeleteModal={setOpenDeleteModal}
                        setSelectedRow={setSelectedRow}
                    />
                )}
                bordered
                stripped={false}
                fixedHeader
                infiniteScroll
                sentinelRef={sentinelRef}
                loadingMore={loading}
                hasMore={hasMore}
                isMultiSelect={false}
            />
            {
                editCodingCategories && (
                    <div class="admin-section-question-list-btn-wrapper">
                        <Button
                            size="sm"
                            hierarchy={{ type: "secondaryGrey", buttonText: "Discard Changes" }}
                            onClick={() => {
                                setEditCodingCategories(false);
                                // console.log("previousRef.current:", previousRef.current[0]);
                                setSections(previousRef.current[0]);
                            }}
                        />
                        <Button
                            size="sm"
                            hierarchy={{
                                type: "primary",
                                buttonText: isLoading ? "Saving.." : "Save Changes",
                            }}
                            disable={isLoading}
                            onClick={() => { updateOrderNumberFunction() }}
                        />
                    </div>
                )
            }

            {/* delete pop up */}
            <Modal
                isOpen={openDeleteModal}
                onClose={() => onCloseModal()}
                showCloseIcon={false}
                className="library-section-question-delete-confirm-modal"
            >
                <ModalBody
                    content={
                        <>
                            <img
                                src={QnDeactivateImg}
                                alt={"Deactivate image"}
                                width={165}
                                height={135}
                            />
                            <div className="library-question-delete-qn">
                                Are you sure you want to{" "}
                                remove the question?
                                <span className="library-question-delete-refer-text">
                                    This question will be hidden from Coding Challenge section for learners.
                                </span>
                            </div>
                        </>
                    }
                    footer={
                        <>
                            <Button
                                size="sm"
                                hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
                                onClick={() => onCloseModal()}
                            />
                            <Button
                                size="sm"
                                hierarchy={{
                                    type: "primary",
                                    buttonText: "Remove",
                                }}
                                onClick={() => deletQuestionFromSection(selectedRow)}
                                disable={isLoading}
                            />
                        </>
                    }
                />
            </Modal>

            {/* add question */}
            <Modal
                isOpen={openQuestionModal}
                onClose={() => onCloseQuestionModal()}
                showCloseIcon={false}
                className="library-section-question-add-modal"
            >
                <SectionQuestion onCloseQuestionModal={onCloseQuestionModal} secid={secid} />
            </Modal>

        </div>
    )
}

const RowActionMenu = ({ row, editCodingCategories, setOpenDeleteModal, setSelectedRow }) => {

    return (
        <div className="library-section-question-delete">
            <div className={`library-section-question-level ${row.complexityname}`}>{row.complexityname}</div>

            <Tooltip
            title="Want to remove the Questions? Click on edit."
            placement="top-start"
            theme="light"
            isIcon
            disableHoverListener={editCodingCategories}
            >
                <IconButton
                    size="md"
                    hierarchy="secondaryGrey"
                    destructive={false}
                    disabled={editCodingCategories ? false : true}
                    onClick={() => {
                        setOpenDeleteModal(true);
                        setSelectedRow(row);
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1H13M1 4H19M17 4L16.2987 14.5193C16.1935 16.0975 16.1409 16.8867 15.8 17.485C15.4999 18.0118 15.0472 18.4353 14.5017 18.6997C13.882 19 13.0911 19 11.5093 19H8.49065C6.90891 19 6.11803 19 5.49834 18.6997C4.95276 18.4353 4.50009 18.0118 4.19998 17.485C3.85911 16.8867 3.8065 16.0975 3.70129 14.5193L3 4" stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </IconButton>
            </Tooltip>
        </div>
    );
};

const RowReorderMenu = ({ row, editCodingCategories, dragRef, rowIndex }) => {
    return (
        <div className="library-section-question-sno" >
            <Tooltip
                title="Want to re-arrange the questions? Click on edit."
                placement="top-start"
                theme="light"
                isIcon
                disableHoverListener={editCodingCategories}
            >
                <IconButton
                    size="md"
                    hierarchy="secondaryGrey"
                    destructive={false}
                    disabled={editCodingCategories ? false : true}
                    onClick={() => {
                        // console.log("hello");
                    }}
                    ref={dragRef}
                >
                    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 7H19M1 1H19M1 13H19" stroke="#98A2B3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </IconButton>
            </Tooltip>
            <div className={`library-section-question-sno-no`}>{rowIndex + 1}</div>
        </div>
    );
};

const DraggableRow = ({
    row,
    rowIndex,
    columns,
    moveRow,
    editCodingCategories,
    setOpenDeleteModal,
    setSelectedRow,
}) => {
    // console.log("row:",row);

    const ref = useRef(null);
    const rowRef = useRef(null);
    const dragRef = useRef(null);

    const [, drop] = useDrop({
        accept: "ROW",

        hover(item) {
            if (item.index === rowIndex) return;
            // item.index = rowIndex;
        },
        drop(item) {
            if (item.index === rowIndex) return;
            moveRow(item.index, rowIndex);
            item.index = rowIndex;
        }
    });

    const [{ isDragging }, drag] = useDrag({
        type: "ROW",

        item: {
            id: row.csid,
            index: rowIndex
        },
        canDrag: () => {
            return editCodingCategories;
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })

    });

    // drag(drop(rowRef));
    drop(rowRef);
    drag(dragRef); // only with the button i can able to drag the row

    return (
        <tr ref={rowRef} class="tableBodyRow" style={{cursor: "default"}}>
            {columns.map((col) => (
                col.key === "sno"
                    ?
                    <td className='tableData sno'>
                        <RowReorderMenu
                            row={row}
                            editCodingCategories={editCodingCategories}
                            dragRef={dragRef}
                            rowIndex={rowIndex}
                        />
                    </td>
                    : col.key === "complexityname"
                        ?
                        <td className='tableData complexityname'>
                            <RowActionMenu
                                row={row}
                                editCodingCategories={editCodingCategories}
                                setOpenDeleteModal={setOpenDeleteModal}
                                setSelectedRow={setSelectedRow}
                            />
                        </td>
                        : <td key={col.key} className='tableData name'>
                            {row[col.key]}
                        </td>
            ))}
        </tr>
    );
}


const SectionQuestion = ({ onCloseQuestionModal, secid }) => {
    const axios = useAxiosPrivate();
    const { setShowNotify } = useContext(AlertContext);

    const [topics, setTopics] = useState([]);
    const [complexities, setComplexities] = useState([]);
    const [complexity, setComplexity] = useState(null)
    const [topic, setTopic] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState([]);
    const [openAddQuestionModal, setOpenAddQuestionModal] = useState(false);

    const [toastType, setToastType] = useState("warning");
    const [openToast, setOpenToast] = useState(false);
    const [errMsg, setErrMsg] = useState("");

    useLayoutEffect(() => {
        fetchDatas();
    }, []);

    const TABLE_COLUMNS = useMemo(
        () => [
            {
                key: "sno",
                title: "S.No",
            },
            {
                key: "name",
                title: "QUESTION",
            },
            {
                key: "topic",
                title: "TOPIC",
            },
            {
                key: "level",
                title: "LEVEL",
            },
            {
                key: "creator",
                title: "CREATED BY",
            },
            {
                key: "section",
                title: "SECTION",
            },
        ],
        [topic, complexity],
    );

    const fetchQuestionsByTopicAndComplexity = useCallback(
        async ({ page, pageSize }) => {
            try {
                // console.log("topic:", topic);
                if (!topic) {
                    return;
                }
                setIsLoading(true);
                const response = await axios.get(`/node/admin/library`, {
                    params: {
                        type: libTypeIds.CODING_CHALLENGE,
                        pageNumber: page,
                        topicId: topic ?? "",
                        complexityid: complexity
                    },
                });
                if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
                    // console.log("response:", response.data.data);

                    const res = typeof response.data.data === "string" ? JSON.parse(response.data.data) : response.data.data;

                    let transformData = res.map((r, index) => ({
                        id: r.id,
                        sno: (page - 1) * pageSize + index + 1,
                        name: r.name,
                        topic: r.topics.map((t) => t.topicname).join(","),
                        level: r.topics.map((t) => t.complexityname).join(","),
                        creator: r.createdby,
                        section: r.sectionname ?? ""
                    }));

                    // console.log("transformedData:", transformData);
                    return { data: transformData, total: response.data.count };
                }
                else {
                    throw new Error("Something went wrong");
                }
            } catch (error) {
                let isUnAuth = error?.response?.status === 403;
                if (error?.name === "CanceledError" || error?.name === "AbortError")
                    return;
                if (isUnAuth) {
                    setShowNotify({
                        show: true,
                        msg: "You have been logged-out due to inactivity. Login again.",
                        title: "Warning !",
                        isUnAuth,
                    });
                }
                setOpenToast(true);
                setErrMsg(error.message ?? "Something went wrong");
                setToastType("error");
            }
            finally {
                setIsLoading(false);
            }
        },
        [axios, setShowNotify, topic, complexity],
    );

    const fetchDatas = async () => {
        try {
            setIsLoading(true);
            const [topics, complexity] = await Promise.all([
                axios.get("node/admin/topic/get/5"),
                axios.get("node/admin/complexity/get"),
            ]);
            if (topics.data.resultCode === RESULT_STATUS.SUCCESS) {
                setTopics(
                    topics.data?.data?.map((topic) => ({
                        value: topic.id,
                        label: topic.name,
                    })),
                );

                setComplexities(
                    complexity?.data?.map((comp) => ({
                        value: comp.id,
                        label: comp.name,
                    })),
                );
            }
            else {
                throw new Error("Something went wrong");
            }
        } catch (error) {
            let isUnAuth = error?.response?.status === 403;
            if (error?.name === "CanceledError" || error?.name === "AbortError")
                return;
            if (isUnAuth) {
                setShowNotify({
                    show: true,
                    msg: "You have been logged-out due to inactivity. Login again.",
                    title: "Warning !",
                    isUnAuth,
                });
            }
            setOpenToast(true);
            setErrMsg(error.message ?? "Something went wrong");
            setToastType("error");
        }
        finally {
            setIsLoading(false);
        }
    }

    const {
        containerRef,
        sentinelRef,
        loading,
        hasMore,
        items: questions,
        setItems: setQuestions,
        refresh,
    } = useInfiniteScroll({
        fetchFn: fetchQuestionsByTopicAndComplexity,
        pageSize: 10,
        resetKey: JSON.stringify({ topic, complexity }),
    });

    // console.log("questions:", questions);

    return (
        <div className='section-question-pop-up-container'>
            <Toast
                open={openToast}
                onClose={() => setOpenToast(false)}
                message={errMsg}
                type={toastType}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            />

            <header className='section-question-pop-up-header-container'>
                <div className='section-question-pop-up-header-name-wrapper' onClick={() => onCloseQuestionModal()}>
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 11L1 6L6 1" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div className='library-section-question-header-title'>Assign</div>
                </div>
                <div className='section-question-pop-up-header-filter-wrapper'>
                    <InputDropdown
                        type="default"
                        placeholder="Choose Topics"
                        options={topics}
                        searchable
                        onChange={(value) => setTopic(value)}
                        childrenPlacement="left"
                        value={topic}
                    />
                    <InputDropdown
                        type="default"
                        placeholder="Choose Level"
                        options={complexities}
                        onChange={(value) => setComplexity(value)}
                        childrenPlacement="left"
                        value={complexity}
                        disabled={!topic}
                    />
                </div>
            </header>
            {
                !topic && (
                    <>
                        <img
                            src={CCQuestionImg}
                            alt={"Question Image"}
                            width="100%"
                            height="100%"
                        />
                        <span style={{ textAlign: "center" }}>Please select the Topic to assign the questions.</span>
                    </>
                )
            }
            {
                topic && (
                    <div className='admin-section-question-table-wrapper'>
                        <Table
                            ref={containerRef}
                            className={`library-section-question-table`}
                            columns={TABLE_COLUMNS}
                            data={questions}
                            bordered
                            stripped={false}
                            fixedHeader
                            infiniteScroll
                            sentinelRef={sentinelRef}
                            loadingMore={loading}
                            hasMore={hasMore}
                            isMultiSelect={true}
                            selectedRows={selectedRow}
                            onRowClick={() => null}
                            onRowSelect={(rows) => {
                                setSelectedRow(rows);
                            }}
                            selectable={true}

                        />
                        <div class="admin-section-question-list-btn-wrapper">
                            <Button
                                size="sm"
                                hierarchy={{ type: "secondaryGrey", buttonText: "Discard" }}
                                onClick={() => {
                                    onCloseQuestionModal()
                                }}
                            />
                            <Button
                                size="sm"
                                hierarchy={{
                                    type: "primary",
                                    buttonText: isLoading ? "Assigning..." : "Assign To",
                                }}
                                disable={isLoading || selectedRow.length < 1}
                                onClick={() => { setOpenAddQuestionModal(true) }}
                            />
                        </div>

                    </div>
                )
            }

            <Modal
                isOpen={openAddQuestionModal}
                onClose={() => setOpenAddQuestionModal(false)}
                showCloseIcon={false}
                className="library-section-question-add-question-modal"
            >
                <AddQuestionModal setOpenAddQuestionModal={setOpenAddQuestionModal} complexities={complexities} secid={secid} selectedRow={selectedRow} setSelectedRow={setSelectedRow} refresh={refresh} />
            </Modal>
        </div>
    );
}


const AddQuestionModal = ({ setOpenAddQuestionModal, complexities, secid, selectedRow, setSelectedRow, refresh }) => {

    const axios = useAxiosPrivate();
    const { setShowNotify } = useContext(AlertContext);

    const [sections, setSections] = useState([]);
    const [section, setSection] = useState(null);
    const [complexity, setComplexity] = useState(null);

    const [isLoading, setIsLoading] = useState(false);

    const [toastType, setToastType] = useState("warning");
    const [openToast, setOpenToast] = useState(false);
    const [errMsg, setErrMsg] = useState("");

    const addQuestionToSection = async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(`node/admin/cc/section/${section}`, {
                qnid: selectedRow,
                complexityid: complexity
            });
            if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
                setOpenToast(true);
                setErrMsg(response.data.data ?? "Question added successfully");
                setToastType("success");
            }
            else if (response.data.resultCode === RESULT_STATUS.NO_DATA) {
                setOpenToast(true);
                setErrMsg(response.data.data ?? "Question added successfully");
                setToastType("warning");
            }
            else {
                throw new Error("Something went wrong");
            }
        } catch (error) {
            let isUnAuth = error?.response?.status === 403;
            if (error?.name === "CanceledError" || error?.name === "AbortError")
                return;
            if (isUnAuth) {
                setShowNotify({
                    show: true,
                    msg: "You have been logged-out due to inactivity. Login again.",
                    title: "Warning !",
                    isUnAuth,
                });
            }
            setOpenToast(true);
            setErrMsg(error.message ?? "Something went wrong");
            setToastType("error");
        }
        finally {
            setIsLoading(false);
        }
    }

    const fetchSectionsFunction = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`node/learner/cc/section?isSOADashboard=true`);
            if (response.data.resultCode === RESULT_STATUS.SUCCESS) {
                // console.log("response:", response.data.data);
                setSections(response.data.data.map((d) => ({
                    value: d.id,
                    label: d.name
                })));
            }
            else {
                throw new Error("Something went wrong");
            }
        } catch (error) {
            let isUnAuth = error?.response?.status === 403;
            if (error?.name === "CanceledError" || error?.name === "AbortError")
                return;
            if (isUnAuth) {
                setShowNotify({
                    show: true,
                    msg: "You have been logged-out due to inactivity. Login again.",
                    title: "Warning !",
                    isUnAuth,
                });
            }
            setOpenToast(true);
            setErrMsg(error.message ?? "Something went wrong");
            setToastType("error");
        }
        finally {
            setIsLoading(false);
        }
    }

    useLayoutEffect(() => {
        fetchSectionsFunction();
    }, []);

    return (
        <div className='section-question-pop-up-add-question-container'>
            <Toast
                open={openToast}
                onClose={() => {
                    if (toastType == "success") {
                        setSelectedRow([]);
                        setOpenAddQuestionModal(false);
                        setOpenToast(false);
                        refresh();
                    }
                    else {
                        setSelectedRow([]);
                        setOpenAddQuestionModal(false);
                        setOpenToast(false);
                    }
                }}
                message={errMsg}
                type={toastType}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            />

            <header className='section-question-pop-up-header-container'>
                <div className='section-question-pop-up-header-name-wrapper' onClick={() => setOpenAddQuestionModal(false)}>
                    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 11L1 6L6 1" stroke="#667085" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div className='library-section-question-header-title'>Assign</div>
                </div>
            </header>
            <div className='section-question-pop-up-header-filter-wrapper'>
                <InputDropdown
                    type="default"
                    placeholder="Choose Topics"
                    options={sections}
                    searchable
                    onChange={(value) => setSection(value)}
                    childrenPlacement="left"
                    value={section}
                />
                <InputDropdown
                    type="default"
                    placeholder="Choose Topics"
                    options={complexities}
                    onChange={(value) => setComplexity(value)}
                    childrenPlacement="left"
                    value={complexity}
                    disabled={!section}
                />
            </div>
            <div class="admin-section-question-list-btn-wrapper">
                <Button
                    size="sm"
                    hierarchy={{ type: "secondaryGrey", buttonText: "Cancel" }}
                    onClick={() => {
                        setOpenAddQuestionModal(false);
                    }}
                />
                <Button
                    size="sm"
                    hierarchy={{
                        type: "primary",
                        buttonText: isLoading ? "Proceeding..." : "Proceed",
                    }}
                    disable={isLoading || !section || !complexity}
                    onClick={() => {
                        addQuestionToSection();
                    }}
                />
            </div>
        </div>
    )
}
export default AdminCcSectionQuestions;